<template>
  <div
    :id="uniqueKey"
    class="fts-form-layouter"
    :class="{
      'form-layouter--drag': drag,
      'form-layouter--ledger': !preview && !document,
      'form-layouter--document': document
    }"
  >
    <draggable
      v-model="form.rows"
      :group="{ name: itemGroup }"
      ghost-class="ghost-row"
      chosen-class="form-element--chosen"
      handle=".row-action-handle"
      :disabled="Boolean(preview)"
      v-bind="dragOptions"
      @change="rowChanged"
      @start="drag=true"
      @end="drag=false"
    >
      <!-- element designer row -->
      <div
        class="element-designer-row"
        v-for="(row, rowIndex) in form.rows"
        :key="row.index"
      >
        <!-- element designer row wrapper -->
        <div class="element-designer-row-wrapper">
          <!-- element designer items -->
          <draggable
            class="element-designer-items"
            :list="row.items"
            :group="{ name: itemGroup }"
            ghost-class="ghost-row"
            chosen-class="form-element--chosen"
            handle=".element-designer-item-action--drag-handle"
            :disabled="Boolean(preview)"
            v-bind="dragOptions"
            @change="changed"
            @start="drag=true"
            @end="drag=false"
          >
            <!-- element designer item -->
            <div
              v-for="(item, ind) in row.items"
              :key="`${ind}-${item.id}`"
              class="element-designer-item"
              :class="{ 'fw-element-item': item.type === 14 || item.type === 15 }"
            >
              <fts-design-element
                :el-parent-id="uniqueKey"
                :modules="modules"
                :activity-id="activityId"
                :document="document"
                :item.sync="row.items[ind]"
                :form="activityDesign"
                :shortcut-options="shortcutOptions"
                :shortcut-elements="shortcutElements"
                :shortcut-selected-options="shortcutSelectedOptions"
                :preview="preview"
                :preview-data="previewData"
                @shortcut-option:selected="addOptionToRow($event, rowIndex + 1)"
                @shortcut-option:create="openInformationDesignModal($event, rowIndex + 1)"
                @added="elementAdded"
                @remove="elementRemoved(row, $event)"
                v-if="item.input === 'section'"
              />
              <fts-design-element
                :el-parent-id="uniqueKey"
                :modules="modules"
                :activity-id="activityId"
                :document="document"
                :item.sync="row.items[ind]"
                :form="activityDesign"
                :shortcut-options="shortcutOptions"
                :shortcut-elements="shortcutElements"
                :shortcut-selected-options="shortcutSelectedOptions"
                :preview="preview"
                :preview-data="previewData"
                @shortcut-option:selected="addOptionToRow($event, rowIndex + 1)"
                @shortcut-option:create="openInformationDesignModal($event, rowIndex + 1)"
                @remove="elementRemoved(row, $event)"
                v-else
              />
            </div>
          </draggable>
        </div>
        <!-- drag zone -->
        <div class="element-designer-drag-zone" v-if="row.items && row.items.length"></div>
      </div>
      <!-- drag zone -->
      <div slot="header" class="element-designer-drag-zone">
      </div>
      <div slot="footer" class="element-designer-drag-zone">
        <!-- control -->
        <fts-form-design-shortcut
          v-if="!preview"
          :modules="modules"
          :options="shortcutOptions"
          :elements="shortcutElements"
          :selected-options="shortcutSelectedOptions"
          suggestion
          placement="bottom-start"
          class="element-designer-row-control"
          @option:selected="addOptionToRow"
          @option:create="openInformationDesignModal"
        >
          <div class="element-designer-row-hint">
            <font-awesome-icon
              :icon="['fts', 'plus']"
              fixed-width
              class="element-designer-row-hint-icon"
            />
            <div class="element-designer-row-hint-label">click to add</div>
          </div>
        </fts-form-design-shortcut>
      </div>
    </draggable>
    <!-- information design modal -->
    <fts-information-design-modal ref="ftsInformationDesignModal" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import canvasDesign from '../commons/canvasDesign';
import commons from '../commons/commons';
import { store as canvasDesignStore } from '../store/canvasDesignStore';

import FTSFormDesignShortcut from './FTSFormDesignShortcut';
import FTSInformationDesignModal from './FTSInformationDesignModal';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';

export default {
  components: {
    draggable,
    "fts-form-design-shortcut": FTSFormDesignShortcut,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-dropdown-select": FTSDropdownSelect
  },
  props: [
    "modules",
    "form",
    "item",
    "itemGroup",
    "informationSource",
    "shortcutOptions",
    "shortcutElements",
    "shortcutSelectedOptions",
    "document",
    "activityId",
    "activityDesign",
    "preview",
    "previewData",
  ],
  data: () => ({
    uniqueKey: null,
    dragOptions: commons.dragOptions,
    drag: false
  }),
  methods: {
    clearUnusedRows() {
      var rows = this.form.rows.slice();
      rows.forEach((row) => {
        if (!row.items || row.items.length == 0) {
          let ind = this.form.rows.indexOf(row);
          this.form.rows.splice(ind, 1);
        }
      });
    },
    elementRemoved(row, item) {
      let ind = row.items.indexOf(item);
      row.items.splice(ind, 1);
      this.clearUnusedRows();
    },
    remove(row) {
      let ind = this.form.rows.indexOf(row);
      this.form.rows.splice(ind, 1);
      this.clearUnusedRows();
    },
    setElement(element) {
      if (this.informationSource && element.id) {
        if (
          element.type == 15
          && (element.design == null || this.item.design.length == 0)
        ) {
          this.$set(element, 'design', { rows: [{ items: []}]});
        }
        commons.expandWorkDesignInternal(
          this.informationSource,
          element
        );
      } else if (element.item) {
        element.item.id = commons.newUUID();
      }
    },
    changed(e) {
      /* SS06064: a field dragged OUT of this row (into a section, or another row) reaches
         this list as `removed` only — the receiving list gets the `added` — so the row it
         left behind kept an empty items[] and rendered as a stray horizontal line. */
      if (e.removed) {
        this.clearUnusedRows();
      }
      if (e.added && e.added.element) {
        this.setElement(e.added.element);
        // if (this.informationSource && e.added.element.id) {
        //   commons.expandWorkDesignInternal(
        //     this.informationSource,
        //     e.added.element
        //   );
        // }
        // if (e.added.element.item) {
        //   e.added.element.item.id = commons.newUUID();
        // }
        this.$emit("added", e.added.element);
        this.clearUnusedRows();
      }
    },
    rowChanged(e) {
      if (e.added && e.added.element) {
        if (e.added.newIndex === -1) {
          this.form.rows.splice(0, 0 , {
            index: commons.newUUID(),
            items: [e.added.element],
          });
        } else {
          const ind = this.form.rows.indexOf(e.added.element);
          this.form.rows.splice(ind, 1, {
            index: commons.newUUID(),
            items: [e.added.element],
          });
        }
        this.setElement(e.added.element);
        // if (this.informationSource && e.added.element.id) {
        //   commons.expandWorkDesignInternal(
        //     this.informationSource,
        //     e.added.element
        //   );
        // }
        // if (e.added.element.item) {
        //   e.added.element.item.id = commons.newUUID();
        // }
        this.$emit("added", e.added.element);
        this.clearUnusedRows();
      }
    },
    elementAdded(elementAdded) {
      if (elementAdded.item) {
        elementAdded.item.id = commons.newUUID();
      }
      this.$emit("added", elementAdded);
    },
    addOptionToRow(element, index) {
      const rowIndex = index ? index : this.form.rows.length;
      if (element) {
        this.form.rows.splice(rowIndex, 0, {
          index: commons.newUUID(),
          items: [element]
        });
        this.setElement(element);
        this.$emit("added", element);
        this.clearUnusedRows();
      }
    },
    async openInformationDesignModal(item, index) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const parentId = this.item && this.item.type == 15 ? this.item.id : null;
      const res = await this.$refs.ftsInformationDesignModal.show(
        canvasDesign.initInformation(item),
        null, null, { parentId: parentId }
      );
      if (res && res !== true) {
        try {
          if (parentId) {
            canvasDesign.setInformation(canvas, _.cloneDeep(res), parentId);
            if (!Array.isArray(this.item.fields)) {
              this.$set(this.item, "fields", [res]);
            } else {
              this.item.fields.push(res);
            }
          } else {
            canvasDesign.setInformation(canvas, _.cloneDeep(res));
          }
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.addOptionToRow({...res}, index);
        } catch(err) {
          console.error(err);
          this.$toasted.global.error();
        }
      }
    }
  },
  computed: {
    isContainsChild() {
      return this.form && Array.isArray(this.form.rows) && this.form.rows.some(x => x.items && x.items.length);
    }
  },
  watch: {
    form: function (val) {
      if (!val) {
        this.form = { rows: [{ items: [] }] };
      } else if (!val.rows) {
        this.form.rows = [{ items: [] }];
      }
    },
  },
  mounted() {
    this.uniqueKey = `form-layouter-${this._uid}`;
  }
};
</script>

<style lang="scss">
@import "~vue-wysiwyg/dist/vueWysiwyg.css";

.fts-form-layouter {
  .element-designer-row-wrapper {
    padding: 0.25rem;
  }
  /* SS05010 (ruled 2026-11-01): a document's Simple tab shows a row the way the PDF prints it -
     every field of the row side by side (templates/document/*.html give each 100/n %) - not the
     form's 2-per-line wrap, which put a row's third field (Sign) on a line of its own */
  &.form-layouter--document > div > .element-designer-row > .element-designer-row-wrapper > .element-designer-items {
    flex-wrap: nowrap;
    > .element-designer-item:not(.fw-element-item) {
      flex: 1 1 0;
      min-width: 0;
    }
  }
  /* ledger mode (design, not preview/document): a hairline between rows keeps the compact
     rows legible — the mock controls that used to delimit them are gone — and the vertical
     rhythm tightens to match the one-line rows */
  &.form-layouter--ledger {
    > div > .element-designer-row + .element-designer-row {
      border-top: 1px solid var(--fts-gray-100);
    }
    > div > .element-designer-row > .element-designer-row-wrapper {
      padding-top: 0.5rem;
      padding-bottom: 0.125rem;
    }
    > div > .element-designer-row .element-designer-items > .element-designer-item {
      margin-bottom: 0.375rem;
    }
    /* the compact rows reserve a real gutter column, so the absolute action strip sits
       beside the label instead of over it; the drag grip stays quietly visible at rest
       (touch-friendly), the add button appears on hover as before */
    .element-designer-item > .fts-design-element {
      padding-left: 2.25rem;
      > .element-designer-item-action-container .element-designer-item-action--drag-handle {
        visibility: visible;
        opacity: 0.35;
      }
      &:hover > .element-designer-item-action-container .element-designer-item-action--drag-handle {
        opacity: 1;
      }
    }
  }
  &.form-layouter--drag {
    > div > .element-designer-item.form-element--chosen {
      padding: 0.25rem 0.25rem 1rem;
      box-shadow: var(--fts-elevation-1);
    }
    .element-designer-row-wrapper {
      &:hover {
        box-shadow: var(--fts-elevation-1);
      }
    }
  }
}
</style>